import { Link, Navigate, useLocation } from 'react-router-dom';
import { PageFrame } from '../components/PageFrame';

type LegalDocument = 'privacy' | 'terms' | 'refund-policy';

type LegalSection = {
  title: string;
  body: Array<string | readonly string[]>;
};

const legalDocuments: Record<LegalDocument, { title: string; intro: string; sections: LegalSection[] }> = {
  privacy: {
    title: 'Privacy Policy',
    intro: 'This policy explains how GHOSTPROBE LTD handles personal information when you use Sanjeom.',
    sections: [
      {
        title: '1. Who controls your information',
        body: ['GHOSTPROBE LTD is the data controller for Sanjeom. Privacy questions can be sent to info@ghostprobe.co.uk.'],
      },
      {
        title: '2. Information we process',
        body: [[
          'Account information, including your email address and authentication identifiers.',
          'Conversation content that you choose to submit, together with AI responses and conversation titles.',
          'Purchase records, package balances, transaction references and payment status. Full card details are handled by Stripe and are not stored by Sanjeom.',
          'Technical and security information such as device type, browser, approximate network location, login events and service diagnostics.',
          'Support messages and any information you choose to include in them.',
        ]],
      },
      {
        title: '3. Why we use it',
        body: [[
          'To create and secure your account, provide conversations and remember your history.',
          'To process purchases, maintain question balances, prevent duplicate fulfilment and handle refunds.',
          'To operate, troubleshoot and protect the service, including fraud and abuse prevention.',
          'To answer support requests and comply with legal, tax and accounting obligations.',
        ], 'We rely on performance of our contract, legitimate interests in operating and protecting the service, consent where required, and compliance with legal obligations.'],
      },
      {
        title: '4. AI processing',
        body: ['The questions and context you submit are sent to contracted AI service providers so that the response you request can be generated. Do not submit information you do not have the right to share. Sanjeom is not designed for emergency, medical, legal or regulated financial advice.'],
      },
      {
        title: '5. Service providers and international transfers',
        body: ['We use service providers for hosting, database and file storage, AI processing, authentication, email delivery, customer support and security. Stripe processes payments as an independent payment provider under its own privacy terms. Where personal information is transferred internationally, we use contractual and legal safeguards appropriate to the transfer.'],
      },
      {
        title: '6. Retention',
        body: ['We keep account and conversation information while your account is active and for as long as needed to provide the service. Payment, fraud-prevention, tax and legal records may be retained for longer where required. When information is no longer needed, it is deleted or de-identified in accordance with our retention process.'],
      },
      {
        title: '7. Your choices and rights',
        body: ['Depending on where you live, you may have rights to access, correct, delete, restrict or object to processing, or receive a portable copy of personal information. You can also withdraw consent where consent is the basis for processing. Contact info@ghostprobe.co.uk to make a request. You may complain to the UK Information Commissioner’s Office or your local supervisory authority.'],
      },
      {
        title: '8. Security and children',
        body: ['We use access controls, encryption in transit, restricted production credentials and monitoring intended to protect the service. No online service is completely secure. Sanjeom is not intended for children under 16, and we do not knowingly offer accounts to them.'],
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    intro: 'These terms govern your use of Sanjeom and your purchase of question packs.',
    sections: [
      {
        title: '1. Operator and agreement',
        body: ['Sanjeom is operated by GHOSTPROBE LTD. By creating an account or purchasing a question pack, you agree to these terms and our Privacy Policy.'],
      },
      {
        title: '2. The service',
        body: ['Sanjeom is an AI-assisted reflection and decision-support tool. Structured prompts provide a foundation for AI-generated explanations. Responses can be incomplete, mistaken or unsuitable for your circumstances. They are not professional medical, legal, financial, employment or emergency advice, and they do not replace your own judgement.'],
      },
      {
        title: '3. Accounts',
        body: ['You must provide current and truthful registration information, keep access credentials secure and notify us if you believe your account has been compromised. Question packs, conversation history and purchase records are tied to your account and must not be resold or transferred.'],
      },
      {
        title: '4. Question packs',
        body: ['Question packs are one-time digital purchases, not subscriptions. One question is one user message submitted for one AI response. Follow-up messages count as additional questions. The current package size and price are shown before checkout. Questions have no cash value and cannot be withdrawn or exchanged.'],
      },
      {
        title: '5. Payments',
        body: ['Prices are displayed in US dollars unless the checkout expressly shows another currency. Stripe processes payment details and may apply its own verification and fraud-prevention checks. Taxes may be added where required. We are responsible for providing the Sanjeom service; Stripe is not the seller.'],
      },
      {
        title: '6. Acceptable use',
        body: [[
          'Do not use the service to break the law, harm others, generate abusive or exploitative content, or interfere with the service.',
          'Do not attempt to bypass question limits, probe security controls, automate abusive traffic or resell access.',
          'Do not submit highly sensitive information unless it is necessary and you have the right to do so.',
        ]],
      },
      {
        title: '7. Availability and changes',
        body: ['We work to keep Sanjeom available, but access can occasionally be interrupted. We may maintain, improve or discontinue parts of the service. Where a material change affects a paid pack, we will take reasonable steps to preserve access or provide an appropriate remedy.'],
      },
      {
        title: '8. Liability and consumer rights',
        body: ['Nothing in these terms excludes liability that cannot legally be excluded or limits your mandatory consumer rights. Subject to that rule, Sanjeom is provided for personal reflection and entertainment, and GHOSTPROBE LTD is not responsible for decisions made solely in reliance on an AI response.'],
      },
      {
        title: '9. Contact and governing law',
        body: ['These terms are governed by the laws of England and Wales, without removing any mandatory protections you have under the law where you live. Contact info@ghostprobe.co.uk with questions or complaints.'],
      },
    ],
  },
  'refund-policy': {
    title: 'Refund Policy',
    intro: 'This policy explains how refunds and digital-service cancellation requests are handled.',
    sections: [
      {
        title: '1. Who handles refunds',
        body: ['GHOSTPROBE LTD is the seller and handles refund requests. Stripe processes the payment but does not decide whether you are entitled to a refund. Contact info@ghostprobe.co.uk and include the account email and Stripe receipt reference. Never send full card details.'],
      },
      {
        title: '2. Before using a question pack',
        body: ['If you are a consumer in the UK or another jurisdiction with a statutory cancellation period, you may ask to cancel an unused digital purchase within the applicable period. For UK consumers this is generally 14 days, subject to the rules for digital content and services.'],
      },
      {
        title: '3. Immediate digital service',
        body: ['At checkout, we ask you to request immediate access. Once you begin using a question pack, your cancellation right may be reduced or lost to the extent permitted by law. This does not affect rights relating to a service that is faulty, misdescribed or not supplied.'],
      },
      {
        title: '4. Service failures and duplicate payments',
        body: ['Tell us if a payment was duplicated, questions were not credited, or the service repeatedly failed to provide the purchased function. We will investigate the account and transaction record and, where appropriate, restore questions or issue a refund to the original payment method.'],
      },
      {
        title: '5. Timing',
        body: ['Approved refunds are submitted to the original payment method. Bank and card-network processing times vary and are outside our control. We will confirm when the refund has been submitted.'],
      },
    ],
  },
};

function isLegalDocument(value: string | undefined): value is LegalDocument {
  return value === 'privacy' || value === 'terms' || value === 'refund-policy';
}

export function LegalPage() {
  const { pathname } = useLocation();
  const document = pathname.replace(/^\//, '');
  if (!isLegalDocument(document)) return <Navigate to="/terms" replace />;
  const content = legalDocuments[document];

  return (
    <PageFrame>
      <section className="legal-hero section-shell">
        <p className="eyebrow">Legal</p>
        <h1>{content.title}</h1>
        <p>{content.intro}</p>
        <p className="legal-updated">Last updated: 5 October 2026</p>
      </section>
      <section className="legal-layout section-shell">
        <nav className="legal-nav" aria-label="Legal documents">
          <Link className={document === 'terms' ? 'active' : ''} to="/terms">Terms</Link>
          <Link className={document === 'privacy' ? 'active' : ''} to="/privacy">Privacy</Link>
          <Link className={document === 'refund-policy' ? 'active' : ''} to="/refund-policy">Refund policy</Link>
        </nav>
        <article className="legal-document">
          {content.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.body.map((block, index) => (
                Array.isArray(block) ? (
                  <ul key={index}>{block.map((item) => <li key={item}>{item}</li>)}</ul>
                ) : (
                  <p key={index}>{block}</p>
                )
              ))}
            </section>
          ))}
        </article>
      </section>
    </PageFrame>
  );
}
